import { useEffect, useRef } from 'react';
import { useSelector } from 'react-redux';

import useUpdatedRef from '../../../utils/react/useUpdatedRef';

import { getOnboardingDisableToolbar } from '../../../workspace/toolbar/selector/onboardingToolbarSelector';

import { RevealKeyboardState } from '../MobileToolbar';

/**
 * Listens to changes in the visual viewport to determine whether keyboard is shown/hidden.
 */
export const useMobileToolbarPositioner = (
    revealKeyboardState: RevealKeyboardState,
    setRevealKeyboardState: (revealKeyboardState: RevealKeyboardState) => void,
    metaToolHideToolbar: boolean,
): void => {
    const animationRef = useRef<number | null>(null);
    const keyboardOpenRef = useRef<boolean>(false);
    const onboardingDisableToolbar = useSelector(getOnboardingDisableToolbar);
    const onboardingDisableToolbarRef = useUpdatedRef(onboardingDisableToolbar);

    // A ref to store the current revealKeyboardState, so that we can access it in the event listeners
    const revealKeyboardStateRef = useUpdatedRef(revealKeyboardState);

    const hideToolbarRef = useRef<boolean>(metaToolHideToolbar);

    useEffect(() => {
        hideToolbarRef.current = metaToolHideToolbar;
    }, [metaToolHideToolbar]);

    const setRevealKeyboardStateAndRef = (newRevealKeyboardState: RevealKeyboardState) => {
        // If the toolbar is disabled, don't shift it into view if the keyboard appears
        if (onboardingDisableToolbarRef.current && newRevealKeyboardState !== RevealKeyboardState.NONE) return;

        revealKeyboardStateRef.current = newRevealKeyboardState;
        setRevealKeyboardState(newRevealKeyboardState);
    };

    const prepareAndReveal = () => {
        // Align it to just below the top of the keyboard, so that the animation will be from this point onwards
        setRevealKeyboardStateAndRef(RevealKeyboardState.PREPARING_REVEAL);

        animationRef.current = requestAnimationFrame(() => {
            setRevealKeyboardStateAndRef(RevealKeyboardState.REVEAL);
            animationRef.current = null;
        });
    };

    useEffect(() => {
        const onKeyboardDidShow = () => {
            keyboardOpenRef.current = true;

            if (
                revealKeyboardStateRef.current === RevealKeyboardState.PREPARING_REVEAL ||
                revealKeyboardStateRef.current === RevealKeyboardState.REVEAL ||
                hideToolbarRef.current
            )
                return;

            prepareAndReveal();
        };

        const onKeyboardWillHide = () => {
            keyboardOpenRef.current = false;

            if (revealKeyboardStateRef.current === RevealKeyboardState.NONE) return;

            if (animationRef.current) {
                cancelAnimationFrame(animationRef.current);
                animationRef.current = null;
            }

            setRevealKeyboardStateAndRef(RevealKeyboardState.NONE);
        };

        window.addEventListener('keyboardDidShow', onKeyboardDidShow);
        window.addEventListener('keyboardWillHide', onKeyboardWillHide);

        return () => {
            window.removeEventListener('keyboardDidShow', onKeyboardDidShow);
            window.removeEventListener('keyboardWillHide', onKeyboardWillHide);
        };
    }, []);

    useEffect(() => {
        // Hide
        if (metaToolHideToolbar) {
            if (revealKeyboardStateRef.current === RevealKeyboardState.PREPARING_REVEAL) {
                setRevealKeyboardStateAndRef(RevealKeyboardState.NONE);
                return;
            }

            // Animate to the preparing reveal state so that it doesn't transition too fast
            setRevealKeyboardStateAndRef(RevealKeyboardState.PREPARING_REVEAL);
            document.addEventListener(
                'transitionend',
                () => {
                    setRevealKeyboardStateAndRef(RevealKeyboardState.NONE);
                },
                { once: true },
            );
        }

        // Reveal
        if (
            keyboardOpenRef.current &&
            revealKeyboardStateRef.current !== RevealKeyboardState.REVEAL &&
            !metaToolHideToolbar
        ) {
            prepareAndReveal();
        }
    }, [metaToolHideToolbar]);
};

export default useMobileToolbarPositioner;
